import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { EDITOR_DEBUG } from '../../utils/editor-debug';

import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  COMMENT_TEXT_TRANSLATION_KEYS,
  CommentType,
  IFormsProvider,
  LiveForm,
  ResourceData,
  SAVE_FORM_RESULT,
  UserInformation
} from '@lfw/roesberg.liveforms.core';
import { EMPTY, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_INITIAL_DATE } from '../../utils/dates';
import { parseFormData } from '../../utils/parseFormData';
import { extractItemId, extractXmlProps, formatXML, parseXML, xmlElementToString } from '../../utils/xmlParser';
import { LiveFormsTranslateLoaderService } from '../services/live-forms-translate-loader.service';
import { DynamicFormItemComponent } from './common-items/DynamicItem/dynamic-form-item.component';
import { EditorToolboxComponent } from './editor-toolbox/editor-toolbox.component';
import { mapFormItemToComponent, SupportedFormItemComponents } from './map-form-item-to-component';
import { EditorSettings } from './types/editor-settings';

interface FirstColumnWidthUpdate {
  id: string;
  width: number;
}

@Component({
  selector: 'lfe-liveforms-editor',
  templateUrl: './liveforms-editor.component.html',
  styleUrls: ['./liveforms-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DynamicFormItemComponent, EditorToolboxComponent]
})
export class LiveformsEditorComponent implements AfterViewInit {
  form = input.required<LiveForm>();
  userInfo = input.required<UserInformation>();
  settings = input.required<EditorSettings>();
  disabled = input(false);

  debug = inject(EDITOR_DEBUG);

  formChanged = output<boolean>();

  liveForm: WritableSignal<LiveForm | undefined> = signal(undefined);
  ResourcesData = signal<Record<string, ResourceData[]>>({});

  dynamicFormItems = viewChildren(DynamicFormItemComponent);
  xmlItems = signal<ChildNode[]>([]);
  sessionCreatedItems = signal<Set<string>>(new Set());

  formGroup = new FormGroup({});
  formProvider = inject(IFormsProvider);
  protected translateService = inject(LiveFormsTranslateLoaderService);

  ngAfterViewInit(): void {
    this.watchForFormControlsAndApplyDisabledState();

    // Override markAsDirty to emit immediately
    const originalMarkAsDirty = this.formGroup.markAsDirty.bind(this.formGroup);
    this.formGroup.markAsDirty = (opts?: { onlySelf?: boolean; emitEvent?: boolean }): void => {
      originalMarkAsDirty(opts);
      this.formChanged.emit(true);
    };
  }

  isItemDescendantOfSection(item: ChildNode, sectionId: string): boolean {
    const descendantsPath = this.getDescendantIdsPathOf(item);
    return descendantsPath.includes(sectionId);
  }

  getDescendantIdsPathOf(item: ChildNode): string[] {
    const currentSectionItemIdPath = [];

    const targetItemProps = extractXmlProps(item);

    const targetItemId = targetItemProps['CustomID'] || targetItemProps['ID'];

    for (const item of this.xmlItems()) {
      const currentProps = extractXmlProps(item);

      const currentId = currentProps['CustomID'] || currentProps['ID'];

      const isSectionOpener = ['SectionItem', 'RepeatSectionItem', 'RepeatGroupSectionItem'].includes(item.nodeName);

      if (isSectionOpener) {
        currentSectionItemIdPath.push(currentId);
      }

      const isSectionEnd = ['SectionEndItem'].includes(item.nodeName);

      if (isSectionEnd) {
        if (currentId === targetItemId) {
          return currentSectionItemIdPath;
        }

        currentSectionItemIdPath.pop();
      }

      if (currentId === targetItemId) {
        return currentSectionItemIdPath;
      }
    }

    return currentSectionItemIdPath;
  }

  getParentSectionOf(item: ChildNode): string {
    const descendantPaths = this.getDescendantIdsPathOf(item);

    if (descendantPaths.length) {
      return descendantPaths[descendantPaths.length - 1];
    }

    return '';
  }

  itemGroupFirstColumnWidths = signal<Record<string, number>>({});
  itemTagsSiblings = [
    'MultiColumnTextItem',
    'TimeInputItem',
    'CalculatedValueItem',
    'NumericInputItem',
    'DateInputItem',
    'CommentItem'
  ];

  firstSiblingsMap = computed(() =>
    this.filteredXmlItems()
      .map((item, index) => ({
        originalIndex: index,
        id: extractItemId(item),
        nodeName: item.nodeName
      }))
      .filter(
        ({ originalIndex, nodeName }, index, arr) =>
          originalIndex === 0 ||
          (!this.itemTagsSiblings.includes(arr[index - 1].nodeName) && this.itemTagsSiblings.includes(nodeName))
      )
  );

  getFirstSiblingId(item: ChildNode): string {
    const currentIndex = this.filteredXmlItems().indexOf(item);
    const currentNodeName = item.nodeName;
    const currentId = extractItemId(item);

    const firstSiblings = this.firstSiblingsMap();

    for (let i = firstSiblings.length - 1; i >= 0; i--) {
      const sibling = firstSiblings[i];
      if (
        this.itemTagsSiblings.includes(sibling.nodeName) &&
        this.itemTagsSiblings.includes(currentNodeName) &&
        sibling.originalIndex <= currentIndex
      ) {
        return sibling.id;
      }
    }

    return currentId;
  }

  filteredXmlItems = computed(() => {
    const allItems = this.xmlItems();

    const mappedItems = allItems.filter((item) => {
      const nodeName = item.nodeName;
      return !!mapFormItemToComponent[nodeName as keyof typeof mapFormItemToComponent];
    });

    // Filter out consecutive DividerItems, keeping only the first one
    const deduplicatedItems: ChildNode[] = [];
    let previousWasDivider = false;

    for (const item of mappedItems) {
      const isDivider = item.nodeName === 'DividerItem';

      if (isDivider && previousWasDivider) {
        // Skip this divider since the previous item was also a divider
        continue;
      }

      deduplicatedItems.push(item);
      previousWasDivider = isDivider;
    }

    return deduplicatedItems;
  });

  getFormItemsXml(formatted = false): string {
    const renderedItems: { id: string; itemXml: string; props: Record<string, unknown> }[] = [];

    this.dynamicFormItems().forEach((dynamicItem) => {
      if (dynamicItem.componentRef) {
        const baseItemInstance = dynamicItem.componentRef.instance as SupportedFormItemComponents;
        const itemXml = baseItemInstance.getXML();
        if (itemXml) {
          const item = parseXML(itemXml).documentElement;
          const props = extractXmlProps(item);
          const id = props['ID'];

          renderedItems.push({ id, itemXml, props });
        }
      }
    });

    const allItems = this.xmlItems();

    let concatenatedFormItems = '';

    for (const index in allItems) {
      const item = allItems[index];

      const props = extractXmlProps(item);
      const id = props['ID'];

      const renderedItem = renderedItems.find((rItem) => rItem.id === id);

      concatenatedFormItems += renderedItem ? renderedItem?.itemXml : xmlElementToString(item);
    }

    const formItemsXmlString = `<FormItems>${concatenatedFormItems}</FormItems>`;
    const output = formatted ? formatXML(formItemsXmlString) : formItemsXmlString;

    if (this.debug) {
      console.log(output);
    }

    return output;
  }

  onResourceDataChanged({ photoItemId, resourceData }: { photoItemId: string; resourceData: ResourceData[] }): void {
    this.ResourcesData.update((currentData) => {
      if (resourceData.length === 0) {
        delete currentData[photoItemId];
        this.formProvider.deleteOfflineFormResourceByFileId(photoItemId);
        return currentData;
      }

      return {
        ...currentData,
        [photoItemId]: [...resourceData]
      };
    });
    this.formChanged.emit(true);
  }

  private cdr = inject(ChangeDetectorRef);

  private _formChangeEffect = effect(() => this.onFormReceived(this.form()));
  private _disabledChangeEffect = effect(() => {
    const _disabled = this.disabled();
    this.updateFormGroupDisabledState();
  });
  private _editorSettingChangeEffect = effect(() => {
    this.translateService.setEditorSettings(this.settings());
  });

  onFormReceived(form: LiveForm): void {
    this.liveForm.set(form);

    const formData = form.formData;
    if (!formData) {
      return;
    }

    this.xmlItems.set(Array.from(parseFormData(this.form().formData).formItems.childNodes).filter(Boolean));
  }

  save(message?: string): Observable<SAVE_FORM_RESULT> {
    const liveForm = this.getUpdatedLiveForm();

    if (!liveForm) {
      return EMPTY;
    }

    const formData = liveForm.formData;

    if (!formData) {
      return EMPTY;
    }

    return this.formProvider.saveForm(liveForm, message);
  }

  private watchForFormControlsAndApplyDisabledState(): void {
    const checkAndApplyDisabled = (): void => {
      this.updateFormGroupDisabledState();

      const controlsCount = Object.keys(this.formGroup.controls).length;
      if (controlsCount === 0) {
        requestAnimationFrame(checkAndApplyDisabled);
      }
    };

    requestAnimationFrame(checkAndApplyDisabled);
  }

  private updateFormGroupDisabledState(): void {
    Promise.resolve().then(() => {
      if (this.disabled()) {
        this.formGroup.disable();
        this.cdr.detectChanges();
      }
    });
  }

  getUpdatedLiveForm(): LiveForm | null {
    const currentLiveform = this.liveForm();

    if (!currentLiveform) {
      console.error('LiveForms not found');
      return null;
    }

    const newFormItems = this.getFormItemsXml();

    const { formOrderPropertyList, liveFormOrder, testScenarios } = parseFormData(currentLiveform.formData);

    const formData = `<liveForm>
${formOrderPropertyList ? `  ${formOrderPropertyList.outerHTML}` : ''}
${liveFormOrder ? `  ${liveFormOrder.outerHTML}` : ''}
${testScenarios ? `  ${testScenarios.outerHTML}` : ''}
  ${newFormItems}
</liveForm>`;

    const resourceData = Object.values(this.ResourcesData()).flat();

    currentLiveform.formData = formData;
    currentLiveform.resourceData = resourceData;

    if (this.debug) {
      console.log(currentLiveform);
    }

    return currentLiveform;
  }

  onAddComment(event: { itemId: string }): void {
    this.insertCommentAfter(CommentType.text, event.itemId);
  }

  onAddPhotoComment(event: { itemId: string }): void {
    this.insertCommentAfter(CommentType.capture, event.itemId);
  }

  onDeleteItem(event: { itemId: string }): void {
    this.deleteItem(event.itemId);
  }

  onRepeatInstanceAdd({ repeatItemId, repeaterItemId }: { repeatItemId: string; repeaterItemId: string }): void {
    console.log('LiveformsEditor received repeat instance add request:', repeatItemId);

    const templateItems = this.getTemplateItemsForRepeat(repeatItemId);

    this.insertRepeatInstanceBefore(repeaterItemId, templateItems);
  }

  onRepeatInstanceDelete({ repeatGroupSectionItemId }: { repeatGroupSectionItemId: string }): void {
    console.log('LiveformsEditor received repeat instance delete request:', repeatGroupSectionItemId);

    this.deleteRepeatInstance(repeatGroupSectionItemId);
  }

  private deleteRepeatInstance(repeatGroupSectionItemId: string): void {
    const items = this.xmlItems().filter((i) => {
      const isChildrenOf = this.isItemDescendantOfSection(i, repeatGroupSectionItemId);

      if (isChildrenOf) {
        return false;
      }
      const props = extractXmlProps(i);

      const currentId = props['CustomID'] || props['ID'];

      return currentId !== repeatGroupSectionItemId;
    });

    this.xmlItems.set(items);

    this.formGroup.markAsDirty();
    this.cdr.detectChanges();
  }

  onUpdateFirstColumnWidth({ id, width }: FirstColumnWidthUpdate): void {
    this.itemGroupFirstColumnWidths.update((widths) => {
      const currentWidth = widths[id] ?? 0;

      // Only update if new width is larger
      if (width > currentWidth) {
        return { ...widths, [id]: width };
      }
      return widths;
    });

    this.cdr.detectChanges();
  }

  isItemCreatedInSession = (itemId: string): boolean => {
    return this.sessionCreatedItems().has(itemId);
  };

  trackByItemId(index: number, item: ChildNode): string {
    return extractItemId(item) || `${item.nodeName}_${index}`;
  }

  private getTemplateItemsForRepeat(repeatItemId: string): string[] {
    const xmlItemsArray = this.xmlItems();
    const templateItems: string[] = [];

    // Find the RepeatSectionItem
    const repeatItemIndex = xmlItemsArray.findIndex((item) => {
      if (item.nodeType === Node.ELEMENT_NODE) {
        const element = item as Element;
        if (element.nodeName !== 'RepeatSectionItem') return false;

        const props = extractXmlProps(element);

        return [props['CustomID'], props['ID']].includes(repeatItemId);
      }
      return false;
    });

    if (repeatItemIndex === -1) {
      console.warn('RepeatSectionItem not found for ID:', repeatItemId);
      return [];
    }

    // Find the corresponding SectionEndItem
    let sectionEndIndex = -1;
    for (let i = repeatItemIndex + 1; i < xmlItemsArray.length; i++) {
      const item = xmlItemsArray[i];
      if (item.nodeType === Node.ELEMENT_NODE) {
        const element = item as Element;
        if (element.nodeName === 'SectionEndItem') {
          sectionEndIndex = i;
          break;
        }
      }
    }

    if (sectionEndIndex === -1) {
      console.warn('SectionEndItem not found after RepeatSectionItem:', repeatItemId);
      return [];
    }

    // Extract all items between RepeatSectionItem and SectionEndItem that have IsGenericRepeatItem="True"
    for (let i = repeatItemIndex + 1; i < sectionEndIndex; i++) {
      const item = xmlItemsArray[i];
      if (item.nodeType === Node.ELEMENT_NODE) {
        const element = item as Element;

        const props = extractXmlProps(element);

        // Check if this is a generic repeat item
        const isGenericRepeatItem = props['IsGenericRepeatItem'];
        if (isGenericRepeatItem === 'True') {
          // Convert to XML string
          const serializer = new XMLSerializer();
          const xmlString = serializer.serializeToString(element);
          templateItems.push(xmlString);
        }
      }
    }

    console.log(`Found ${templateItems.length} template items for RepeatItem ${repeatItemId}`);
    return templateItems;
  }

  private insertRepeatInstanceBefore(repeaterItemId: string, templateItems: string[]): void {
    const xmlItemsArray = this.xmlItems();

    // Find the repeat item
    const repeaterItemIndex = xmlItemsArray.findIndex((item) => {
      if (item.nodeType === Node.ELEMENT_NODE) {
        const element = item as Element;

        const props = extractXmlProps(element);

        return [props['CustomID'], props['ID']].includes(repeaterItemId);
      }
      return false;
    });

    if (repeaterItemIndex === -1) {
      console.error('RepeatItem with ID not found:', repeaterItemIndex);
      return;
    }

    // Find the insertion point: after the last existing repeat instance for this RepeatItem
    const insertIndex = repeaterItemIndex - 1;

    // Parse templateItems XML strings and create XML nodes
    const templateNodes: ChildNode[] = [];

    // Create a mapping of old IDs to new UUIDs for this instance
    const idMapping = new Map<string, string>();
    const customIdMapping = new Map<string, string>();

    // First pass: collect all IDs and CustomIDs that need to be replaced
    for (const templateXml of templateItems) {
      try {
        const parsedXml = parseXML(templateXml);
        if (parsedXml.documentElement) {
          const element = parsedXml.documentElement;

          const props = extractXmlProps(element);

          const customId = props['CustomID'];
          if (customId) {
            const oldId = customId;
            if (!customIdMapping.has(oldId)) {
              customIdMapping.set(oldId, uuidv4());
            }
          }

          const currentId = props['ID'];
          if (currentId) {
            const oldId = currentId;
            if (!idMapping.has(oldId)) {
              idMapping.set(oldId, uuidv4());
            }
          }
        }
      } catch (error) {
        console.error('Failed to parse template XML for ID collection:', templateXml, error);
      }
    }

    // Second pass: clone templates and replace all IDs
    for (const templateXml of templateItems) {
      try {
        const parsedXml = parseXML(templateXml);
        if (parsedXml.documentElement) {
          // Clone the template
          const clonedNode = parsedXml.documentElement.cloneNode(true) as Element;

          // Replace IDs and CustomIDs with new UUIDs
          const idElement = Array.from(clonedNode.children).find((child) => child.nodeName === 'ID');
          if (idElement && idElement.textContent) {
            const oldId = idElement.textContent;
            const newId = idMapping.get(oldId);
            if (newId) {
              idElement.textContent = newId;
            }
          }

          const customIdElement = Array.from(clonedNode.children).find((child) => child.nodeName === 'CustomID');
          if (customIdElement && customIdElement.textContent) {
            const oldCustomId = customIdElement.textContent;
            const newCustomId = customIdMapping.get(oldCustomId);
            if (newCustomId) {
              customIdElement.textContent = newCustomId;
            }
          }

          // Replace ID references in ActivationRule and any Formula fields
          this.updateIdReferencesInElement(clonedNode, idMapping);
          this.updateIdReferencesInElement(clonedNode, customIdMapping);

          // Set IsGenericRepeatItem to False for the actual instances
          const isGenericElement = Array.from(clonedNode.children).find(
            (child) => child.nodeName === 'IsGenericRepeatItem'
          );
          if (isGenericElement) {
            isGenericElement.textContent = 'False';
          } else {
            // Add IsGenericRepeatItem element if it doesn't exist
            const newElement = clonedNode.ownerDocument.createElement('IsGenericRepeatItem');
            newElement.textContent = 'False';
            clonedNode.appendChild(newElement);
          }

          templateNodes.push(clonedNode);
        }
      } catch (error) {
        console.error('Failed to parse template XML:', templateXml, error);
      }
    }

    const repeatGroupSectionItem = this.getRepeatGroupSectionItem();

    const sectionEndItem = this.getRepeatGroupEndSectionItem();

    if (templateNodes.length > 0) {
      // Insert the template nodes at the calculated position
      this.xmlItems.update((items) => [
        ...items.slice(0, insertIndex),
        repeatGroupSectionItem,
        ...templateNodes,
        sectionEndItem,
        ...items.slice(insertIndex)
      ]);

      console.log(`Inserted ${templateNodes.length} template items after index:`, insertIndex);
    }

    this.formGroup.markAsDirty();
    this.cdr.detectChanges();
  }

  private getRepeatGroupSectionItem(): Element {
    return parseXML(`<RepeatGroupSectionItem>
      <ID>${uuidv4()}</ID>
      <IsGenericRepeatItem>False</IsGenericRepeatItem>
      <Style>Normal</Style>
    </RepeatGroupSectionItem>`).documentElement;
  }

  private getRepeatGroupEndSectionItem(): Element {
    return parseXML(`<SectionEndItem>
      <ID>${uuidv4()}</ID>
      <IsGenericRepeatItem>False</IsGenericRepeatItem>
      <Style>Normal</Style>
    </SectionEndItem>`).documentElement;
  }

  private updateIdReferencesInElement(element: Element, idMapping: Map<string, string>): void {
    // Fields that may contain ID references that need to be updated
    const fieldsToUpdate = ['ActivationRule', 'EquationFormula'];

    const props = extractXmlProps(element);

    for (const fieldName of fieldsToUpdate) {
      if (Object.keys(props).includes(fieldName)) {
        let updatedContent = props[fieldName];

        for (const [oldId, newId] of idMapping.entries()) {
          const regex = new RegExp(`\\b${oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
          updatedContent = updatedContent.replace(regex, newId);
        }

        const childToUpdateContent = Array.from(element.childNodes).find((node) => node?.nodeName === fieldName);

        if (childToUpdateContent) {
          childToUpdateContent.textContent = updatedContent;
        }
      }
    }
  }

  private insertCommentAfter(type: CommentType, itemId: string): void {
    const xmlItemsArray = this.xmlItems();
    const xmlItemIndex = xmlItemsArray.findIndex((item) => {
      if (item.nodeType === Node.ELEMENT_NODE) {
        const element = item as Element;

        const props = extractXmlProps(element);

        const customId = props['CustomID'];
        const id = props['ID'];
        if ([customId, id].includes(itemId)) {
          return true;
        }
      }
      return false;
    });

    if (xmlItemIndex === -1) {
      console.error('Item with ID not found:', itemId);
      return;
    }

    const { xml: commentXml, id: newCommentId } = this.createNewCommentXml(type, itemId);
    const commentNode = parseXML(commentXml).documentElement;

    this.sessionCreatedItems.update((comments) => new Set([...comments, newCommentId]));

    this.xmlItems.update((items) => [
      ...items.slice(0, xmlItemIndex + 1),
      commentNode,
      ...items.slice(xmlItemIndex + 1)
    ]);

    this.formGroup.markAsDirty();

    this.cdr.detectChanges();
  }

  private deleteItem(itemId: string): void {
    this.sessionCreatedItems.update((comments) => {
      const newComments = new Set(comments);
      newComments.delete(itemId);
      return newComments;
    });

    // Find and remove the item with the given ID
    this.xmlItems.update((items) =>
      items.filter((item) => {
        if (item.nodeType === Node.ELEMENT_NODE) {
          const element = item as Element;

          const props = extractXmlProps(element);

          const customId = props['CustomID'];
          const id = props['ID'];
          if ([customId, id].includes(itemId)) {
            return false;
          }
        }
        return true; // Keep this item
      })
    );

    // Mark form as dirty to trigger change detection
    this.formGroup.markAsDirty();
    this.cdr.detectChanges();
  }

  private createNewCommentXml(type: CommentType, relatedItemId: string): { xml: string; id: string } {
    const newId = uuidv4();
    const translationKey = COMMENT_TEXT_TRANSLATION_KEYS[type];
    const text = this.translateService.instant(translationKey);
    const userName = this.userInfo().displayName;

    const xmlStringItem = {
      [CommentType.text]: `<CommentItem>
  <ID>${newId}</ID>
  <IsGenericRepeatItem>False</IsGenericRepeatItem>
  <RelatedItemID>${relatedItemId}</RelatedItemID>
  <Text>${text}</Text>
  <UserName>${userName}</UserName>
  <AddedWhileProcessing>True</AddedWhileProcessing>
  <ResultTime>${DEFAULT_INITIAL_DATE}</ResultTime>
</CommentItem>`,
      [CommentType.capture]: `<PhotoItem>
  <AddedWhileProcessing>True</AddedWhileProcessing>
  <ID>${newId}</ID>
  <IsGenericRepeatItem>False</IsGenericRepeatItem>
  <Text>${text}</Text>
  <UserName>${userName}</UserName>
  <RelatedItemID>${relatedItemId}</RelatedItemID>
  <ResultTime>${new Date().toISOString()}</ResultTime>
  <ResultUser>${this.userInfo().displayName}</ResultUser>
</PhotoItem>`,

      [CommentType.image]: `<></>`
    };

    return { xml: xmlStringItem[type], id: newId };
  }
}
